import { RedirectToSignIn, UserButton } from '@clerk/react';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectSession } from '@client/model/selectors';
import { SessionType } from '@shared';
import { WaterlilyLogo } from '@client/components/WaterlilyLogo';
import { Spinner } from '@design-system';
import { Link } from 'react-router-dom';
import { UnauthorizedPage } from '@client/components/UnauthorizedPage';

export function ClientLogin() {
  const { isClerkLoading, isClerkSignedIn } = useClerkClientAuth();
  const session = useSelector(selectSession);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect_to');
  const emailHint = searchParams.get('email');

  // Only redirect after the server session is fully loaded (not just Clerk auth)
  useEffect(() => {
    if (session.sessionType === SessionType.client) {
      navigate(redirectTo || '/client/home', { replace: true });
    }
  }, [session.sessionType, navigate, redirectTo]);

  // Advisor/wizard already signed in via Auth0 — block access to client login
  if (session.sessionType === SessionType.advisor || session.sessionType === SessionType.wizard) {
    return <UnauthorizedPage />;
  }

  // Wait for both Clerk and server session to resolve before deciding auth flow.
  // Without this, an Auth0 advisor (isClerkSignedIn=false) would hit <RedirectToSignIn>
  // before the session type resolves to advisor.
  if (isClerkLoading || session.sessionType === SessionType.unknown) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Spinner className="size-8 text-primary" />
      </div>
    );
  }

  // Not signed in — redirect directly to Clerk sign-in
  if (!isClerkSignedIn) {
    const afterSignInUrl = redirectTo
      ? `/client/login?redirect_to=${encodeURIComponent(redirectTo)}`
      : '/client/login';
    return (
      <RedirectToSignIn
        signInFallbackRedirectUrl={afterSignInUrl}
        {...(emailHint ? { initialValues: { emailAddress: emailHint } } : {})}
      />
    );
  }

  // Signed in — show loading/error while server session loads
  const isSessionLoading =
    session.sessionType !== SessionType.client && session.sessionType !== SessionType.unauthenticated;

  const hasSessionError = session.sessionType === SessionType.unauthenticated;

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="fixed left-0 right-0 top-0 z-50 flex items-center justify-between border-b bg-white px-4 py-4 md:px-6">
        <WaterlilyLogo className="h-6" />
        <Link
          to="/"
          className="text-sm text-gray-600 transition-colors duration-200 hover:text-gray-800"
        >
          Advisor sign-in
        </Link>
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-4 pb-12 pt-18 md:pt-20">
        <div className="w-full max-w-md space-y-5">
          <div className="rounded-2xl border bg-background p-1 shadow-none">
            <div className="rounded-xl bg-muted px-4 py-8 text-center">
              <h1 className="text-xl font-bold text-gray-950">
                Sign in to view your results
              </h1>
              <p className="mx-auto mt-1 max-w-xs text-sm text-foreground">
                Access your long-term care planning intake form results
              </p>
            </div>
            <div className="p-6">
              <div className="flex flex-col items-center gap-4">
                <UserButton />
                {isSessionLoading && (
                  <div className="flex items-center gap-2">
                    <Spinner className="size-4 text-primary" />
                    <p className="text-sm text-gray-600">Loading your session...</p>
                  </div>
                )}
                {hasSessionError && (
                  <div className="text-center">
                    <p className="text-sm text-red-600">
                      Unable to load your session. Your email may not be linked to a client profile yet.
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Please contact your advisor or try again.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
